import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RubyLoader } from '../RubyLoader';

/**
 * TERMINAL ACTIVITY — what this till is doing right now (approved mockup
 * 2026-10-02): Connection · Signed in · Shift · Last sale.
 *
 * One backend read (pos-register GET /pos/terminals/{id}/activity), the same
 * one the merchant terminal page (platform-app) uses, so the two never disagree.
 * Super admin reads it with the admin-pool token + x-tenant-id (super admins only).
 * Re-reads every 15s, when the tab comes back into view, and whenever a
 * `terminal-activity-refresh` window event fires (e.g. after a control push).
 *
 * Shape mirrors `APosTerminalActivity` in @platform/shared-types (kept local
 * and structural here so this page does not need a shared-types release).
 */
export interface TerminalActivityData {
  connection: { online: boolean; lastSeenAt: string | null; offlineSince: string | null; heartbeat: boolean };
  signedIn: { employeeId: string; employeeName: string; at: string } | null;
  lastSignOut: { employeeId: string; employeeName: string; at: string; reason: string } | null;
  shift: {
    state: 'open' | 'closed' | 'none';
    openedAt: string | null;
    openedBy: { employeeName: string | null; system?: boolean } | null;
    startingCash: number | null;
    closedAt: string | null;
    closedBy: { employeeName: string | null; system?: boolean } | null;
    counted: boolean;
    variance: number | null;
    autoClosed: boolean;
  };
  lastSale: { at: string; total: number; type: string; employeeName: string | null } | null;
  generatedAt: string;
}

const POLL_MS = 15_000;

// ── formatting ────────────────────────────────────────────────────────────
const money = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function clock(iso: string, now: number): string {
  const d = new Date(iso);
  const t = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const today = new Date(now);
  const yesterday = new Date(now - 86_400_000);
  if (d.toDateString() === today.toDateString()) return t;
  if (d.toDateString() === yesterday.toDateString()) return `${t} yesterday`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${t}`;
}

function span(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} days`;
}

const REASON: Record<string, { label: string; cls: string }> = {
  signed_out: { label: 'Signed out', cls: 'bg-stone-100 text-stone-600 border-stone-200' },
  idle_timeout: { label: 'Idle timeout', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  session_expired: { label: 'Session expired', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  locked: { label: 'Locked by admin', cls: 'bg-red-50 text-red-700 border-red-200' },
  deregistered: { label: 'Deregistered', cls: 'bg-red-50 text-red-700 border-red-200' },
  shift_closed: { label: 'Shift closed', cls: 'bg-stone-100 text-stone-600 border-stone-200' },
};

const who = (p: { employeeName: string | null; system?: boolean } | null) =>
  p?.system ? 'the system' : (p?.employeeName || 'someone');

// ── icons (inline, as in the mockup) ────────────────────────────────────────
const PersonIcon = ({ c }: { c: string }) => (
  <svg width="16" height="16" fill="none" stroke={c} strokeWidth="2" viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></svg>
);
const DrawerIcon = ({ c }: { c: string }) => (
  <svg width="16" height="16" fill="none" stroke={c} strokeWidth="2" viewBox="0 0 24 24" aria-hidden><rect x="3" y="7" width="18" height="12" rx="2" /><path d="M7 7V5h10v2" /></svg>
);
const ReceiptIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#78716c" strokeWidth="2" viewBox="0 0 24 24" aria-hidden><path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" /><path d="M9 7h6M9 11h6" /></svg>
);

const Row: React.FC<{ tile: string; icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ tile, icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className={`w-9 h-9 rounded-lg ${tile} flex items-center justify-center shrink-0`}>{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-stone-500">{label}</p>
      {children}
    </div>
  </div>
);

export const TerminalActivityBody: React.FC<{ a: TerminalActivityData; now: number }> = ({ a, now }) => {
  const { connection: c, signedIn, lastSignOut, shift, lastSale } = a;
  const reason = lastSignOut ? (REASON[lastSignOut.reason] || REASON.signed_out) : null;
  return (
    <div className="space-y-3">
      {/* Connection */}
      {c.online ? (
        <Row tile="bg-emerald-100" icon={<span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />} label="Connection">
          <p className="text-sm font-medium text-emerald-800">Online</p>
          {c.lastSeenAt && <p className="text-xs text-stone-500">Last heard from {span(now - Date.parse(c.lastSeenAt))} ago</p>}
        </Row>
      ) : (
        <Row tile="bg-red-50" icon={<span className="w-2.5 h-2.5 rounded-full bg-red-400" />} label="Connection">
          <p className="text-sm font-medium text-red-600">Offline</p>
          <p className="text-xs text-stone-500">
            {c.offlineSince
              ? `Since ${clock(c.offlineSince, now)} · no heartbeat for ${span(now - Date.parse(c.offlineSince))}`
              : 'Not connected'}
          </p>
        </Row>
      )}

      {/* Signed in */}
      {signedIn ? (
        <Row tile="bg-emerald-100" icon={<PersonIcon c="#059669" />} label="Signed in">
          <p className="text-sm font-medium text-stone-800 truncate">{signedIn.employeeName}</p>
          {signedIn.at && <p className="text-xs text-stone-500">Since {clock(signedIn.at, now)} · {span(now - Date.parse(signedIn.at))}</p>}
        </Row>
      ) : (
        <Row tile="bg-stone-100" icon={<PersonIcon c="#a8a29e" />} label="Signed in">
          <p className="text-sm font-medium text-stone-500">Nobody</p>
          {lastSignOut && (
            <>
              <p className="text-xs text-stone-500 truncate">Last sign-out {clock(lastSignOut.at, now)} · {lastSignOut.employeeName}</p>
              {reason && <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border ${reason.cls}`}>{reason.label}</span>}
            </>
          )}
        </Row>
      )}

      {/* Shift */}
      {shift.state === 'open' ? (
        <Row tile="bg-emerald-100" icon={<DrawerIcon c="#059669" />} label="Shift">
          <p className="text-sm font-medium text-stone-800">Open{shift.openedAt ? ` since ${clock(shift.openedAt, now)}` : ''}</p>
          <p className="text-xs text-stone-500">
            Opened by {who(shift.openedBy)}
            {signedIn
              ? (shift.startingCash != null ? ` · ${money(shift.startingCash)} float` : '')
              : ' · signing out does not close it'}
          </p>
        </Row>
      ) : shift.state === 'closed' ? (
        <Row tile="bg-stone-100" icon={<DrawerIcon c="#a8a29e" />} label="Shift">
          <p className="text-sm font-medium text-stone-700">Closed{shift.closedAt ? ` ${clock(shift.closedAt, now)}` : ''}</p>
          <p className="text-xs text-stone-500">
            by {who(shift.closedBy)}
            {shift.counted
              ? ` · counted${shift.variance != null ? `, ${money(shift.variance)} variance` : ''}`
              : shift.autoClosed ? ' · auto-closed, drawer not counted' : ''}
          </p>
        </Row>
      ) : (
        <Row tile="bg-stone-100" icon={<DrawerIcon c="#a8a29e" />} label="Shift">
          <p className="text-sm font-medium text-stone-500">No shift yet</p>
        </Row>
      )}

      {/* Last sale */}
      <Row tile="bg-stone-100" icon={<ReceiptIcon />} label="Last sale">
        {lastSale ? (
          <>
            <p className="text-sm font-medium text-stone-800">
              {clock(lastSale.at, now)} · {lastSale.type === 'refund' ? 'Refund ' : ''}{money(lastSale.total)}
            </p>
            {lastSale.employeeName && <p className="text-xs text-stone-500 truncate">by {lastSale.employeeName}</p>}
          </>
        ) : (
          <p className="text-sm font-medium text-stone-500">None recorded yet</p>
        )}
      </Row>
    </div>
  );
};

const TerminalActivityCard: React.FC<{ load: () => Promise<TerminalActivityData> }> = ({ load }) => {
  const [data, setData] = useState<TerminalActivityData | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const loadRef = useRef(load);
  loadRef.current = load;
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const a = await loadRef.current();
      setData(a);
      setFetchedAt(Date.now());
      setError(false);
    } catch {
      setError(true);
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const poll = window.setInterval(() => { if (!document.hidden) void refresh(); }, POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 5_000);
    const onVisible = () => { if (!document.hidden) void refresh(); };
    const onEvent = () => { void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('terminal-activity-refresh', onEvent);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('terminal-activity-refresh', onEvent);
    };
  }, [refresh]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-stone-800 font-semibold text-sm inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-600" aria-hidden />
          Activity
        </h3>
        <span className="text-[10px] text-stone-400">
          {error && !data ? 'could not load' : fetchedAt ? `updated ${span(now - fetchedAt)} ago` : ''}
          {error && data ? ' · retrying' : ''}
        </span>
      </div>
      {data ? (
        <TerminalActivityBody a={data} now={now} />
      ) : error ? (
        <p className="text-sm text-stone-500">Activity is unavailable right now — it will retry automatically.</p>
      ) : (
        <div className="relative space-y-3" aria-busy>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-stone-100" />
              <div className="flex-1 space-y-1.5 pt-0.5">
                <div className="h-2.5 w-16 rounded bg-stone-100" />
                <div className="h-3 w-32 rounded bg-stone-100" />
              </div>
            </div>
          ))}
          <RubyLoader variant="overlay" size={32} />
        </div>
      )}
    </div>
  );
};

export default TerminalActivityCard;
